import './bootstrap'
import '../css/app.css'
import '../css/fonts.css'
import '../css/theme.css'
import '../css/dynamic-fonts.css'
import '../css/transitions.css'

import lang from './translations.js'

import {
    getModelUrl,
    isCurrentLocalizedRoute,
    localizedRoute,
} from './Tools/routeUtilities.js'
import { getBaseModelTranslation } from './Tools/localizationUtilities.js'

import moment from 'moment/min/moment-with-locales'
import mitt from 'mitt'

import { createPinia } from 'pinia'
import { createApp, h } from 'vue'
import { createInertiaApp, usePage } from '@inertiajs/vue3'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import { ZiggyVue } from '../../vendor/tightenco/ziggy'
import PrimeVue from 'primevue/config'
import PrimeVueLocaleSv from 'primelocale/sv.json'
import PrimeVueLocaleEn from 'primelocale/en.json'
const PrimeVueLocales = {
    sv: PrimeVueLocaleSv.sv,
    en: PrimeVueLocaleEn.en,
}

const appName = import.meta.env.VITE_APP_NAME || 'Laravel'

import MainLayout from '@/Layouts/MainLayout/MainLayout.vue'
import LandingPageLayout from '@/Layouts/LandingPageLayout.vue'
createInertiaApp({
    title: (title) => (title ? `${title} - ${appName}` : appName),
    resolve: (name) => {
        const page = resolvePageComponent(
            `./Pages/${name}.vue`,
            import.meta.glob('./Pages/**/*.vue')
        )
        page.then((module) => {
            // If a layout has not been defined, set the layout based on the page name
            if (!module.default.layout && !['Error'].includes(name)) {
                // If the page name starts with 'LandingPages' or 'Auth', use the LandingPageLayout
                if (
                    name.startsWith('LandingPages') ||
                    name.startsWith('Auth') ||
                    ['PrivacyPolicy', 'TermsOfService'].includes(name)
                ) {
                    module.default.layout = LandingPageLayout
                    // Otherwise, use the MainLayout
                } else {
                    module.default.layout = MainLayout
                }
            }
        })
        return page
    },
    async setup({ el, App, props, plugin }) {
        const VueApp = createApp({ render: () => h(App, props) })
        const pinia = createPinia()
        VueApp.use(pinia)

        setupGlobalProperties(VueApp, props.initialPage.props)

        VueApp.use(PrimeVue, {
            unstyled: true,
            locale: PrimeVueLocales[props.initialPage.props.locale],
        })
        VueApp.use(plugin).use(ZiggyVue).mount(el)

        await setupSentry(VueApp)
        setupMatomo(VueApp)
    },
    progress: {
        color: '#4B5563',
    },
})

function setupGlobalProperties(app, props) {
    setupLocale(app, props)
    setupMoment(app)
    setupModelUrl(app)
    setupRouteUtilities(app)
    setupBaseModelTranslation(app)
    setupGetAvailableModels(app, props)
    setupIsAdmin(app)
    setupIsModerator(app)
    setupBus(app)
}

function setupLocale(app, props) {
    app.config.globalProperties.$lang = lang
    app.config.globalProperties.$lang.setLocale(props.locale)
    lang.setLocale(props.locale)
}

function setupMoment(app) {
    moment.locale(app.config.globalProperties.$lang.getLocale())
    app.config.globalProperties.$moment = moment
}

function setupModelUrl(app) {
    app.config.globalProperties.$getModelUrl = getModelUrl
}

function setupRouteUtilities(app) {
    app.config.globalProperties.$localizedRoute = localizedRoute
    app.config.globalProperties.$isCurrentLocalizedRoute =
        isCurrentLocalizedRoute
}

function setupBaseModelTranslation(app) {
    app.config.globalProperties.$getBaseModelTranslation =
        getBaseModelTranslation
}

function setupBus(app) {
    const emitter = mitt()
    app.config.globalProperties.$bus = emitter
}

/**
 * Returns the avaiable models in the system
 * @param {boolean} includeHidden - If true, the hidden models will be included. User is hidden for normal users
 */
function setupGetAvailableModels(app, props) {
    app.config.globalProperties.$getAvailableModels = () =>
        props.availableModels
}

function setupIsAdmin(app) {
    Object.defineProperty(app.config.globalProperties, '$isAdmin', {
        get: function () {
            return usePage().props.auth?.user?.isAdmin || false
        },
    })
}

function setupIsModerator(app) {
    Object.defineProperty(app.config.globalProperties, '$isModerator', {
        get: function () {
            return usePage().props.auth?.user?.isModerator || false
        },
    })
}

/**
 * Setup matomo if the host and site id is set
 */
function setupMatomo(app) {
    if (import.meta.env.VITE_MATOMO_HOST) {
        import('vue-matomo').then(({ default: VueMatomo }) => {
            app.use(VueMatomo, {
                host: import.meta.env.VITE_MATOMO_HOST,
                siteId: import.meta.env.VITE_MATOMO_SITE_ID,
                enableHeartBeatTimer: true, // Better tracking of visit times
                enableLinkTracking: true, // Track clicks on links
            })
        })
    }
}

function setupSentry(app) {
    if (!import.meta.env.VITE_JS_SENTRY_DSN) {
        return
    }

    return import('@sentry/vue')
        .then((Sentry) => {
            Sentry.init({
                app: app,
                environment: import.meta.env.DEV ? 'Development' : 'Production',
                dsn: import.meta.env.VITE_JS_SENTRY_DSN,
                integrations: [],
                initialScope: {
                    tags: {
                        system: import.meta.env.VITE_APP_NAME,
                        locale: lang?.locale || '-',
                    },
                },
            })
        })
        .finally(() => {
            return
        })
}
